import styled from 'styled-components'
import { black, darkOrange, surface2, textMuted, white } from '@/theme/colors'
import { Button } from '@components/Button'
import { DropdownChevron, DropdownMenu } from '@components/Dropdown'
import { layers, radii, shadows } from '@/theme/tokens'
import { text } from '@/theme/typography'
import { destinationColor } from '../destinationPalette'

export const MapRoot = styled.div`
	position: relative;
	width: 100%;
	height: 100%;
	background: ${surface2};
	isolation: isolate;
`

export const MapTarget = styled.div`
	position: absolute;
	inset: 0;
`

export const OverlayRow = styled.div`
	position: absolute;
	top: 16px;
	left: 16px;
	right: 16px;
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	gap: 8px;
	z-index: ${layers.raised};
	pointer-events: none;
`

/** The floating label over the map: the card's white on the overlay shadow. */
export const MapLabel = styled.div`
	${text.labelSm}
	color: ${darkOrange};
	background: ${white};
	border-radius: ${radii.inner};
	padding: 7px 12px;
	box-shadow: ${shadows.overlay};
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	min-width: 0;
`

/**
 * The day's label as a pill chip on the selected calendar cell's colour, and
 * the trigger of the day picker under it. The destination fill replaces the
 * primary orange, so hover dims rather than swapping to the orange's dark step.
 */
export const DayChip = styled(Button).attrs({ variant: 'primary' as const, size: 'sm' as const, shape: 'pill' as const })<{
	$colorIndex: number
}>`
	pointer-events: auto;
	min-width: 0;
	gap: 2px;
	padding: 6px 8px 6px 14px;
	background: ${({ $colorIndex }) => destinationColor($colorIndex).fill};
	box-shadow: ${shadows.overlay};

	&:hover:not(:disabled) {
		background: ${({ $colorIndex }) => destinationColor($colorIndex).fill};
		filter: brightness(0.92);
	}

	&[aria-expanded='true'] {
		box-shadow:
			${shadows.overlay},
			${shadows.focus};
	}
`

/** The Dropdown's chevron in the chip's own white, not the field's muted grey. */
export const ChipChevron = styled(DropdownChevron)`
	color: inherit;
`

export const ChipText = styled.span`
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
`

/** The picker's list: the Dropdown's panel, sized to its labels rather than to the chip. */
export const DayMenu = styled(DropdownMenu)`
	width: auto;
	min-width: ${({ $width }) => Math.max($width, 240)}px;
	max-width: min(360px, calc(100vw - 32px));
`

export const OverlayActions = styled.div`
	display: flex;
	gap: 8px;
	flex: none;
	pointer-events: auto;
`

export const MapChip = styled(Button).attrs({ size: 'sm' as const })`
	box-shadow: ${shadows.overlay};
`

export const StatusOverlay = styled.div`
	position: absolute;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	background: ${surface2};
	color: ${textMuted};
	${text.bodySm}
	z-index: ${layers.raised};
	pointer-events: none;
`

export const NoPinsCard = styled.div`
	position: absolute;
	left: 50%;
	top: 50%;
	transform: translate(-50%, -50%);
	z-index: ${layers.raised};
	pointer-events: none;
	background: ${white};
	border-radius: ${radii.inner};
	padding: 12px 18px;
	box-shadow: ${shadows.overlay};
	${text.bodyXs}
	color: ${textMuted};
	text-align: center;
	white-space: nowrap;
`

export const NoPinsTitle = styled.b`
	display: block;
	color: ${black};
`
